import { memo } from 'react';
import { motion } from 'framer-motion';
import { Color, PieceType } from '@fog-chess/core';
import { Piece } from './Piece';

/** Visual mode for the transient move overlay. */
export type MoveGhostMode = 'piece' | 'fog';

export interface MoveGhostProps {
  /** Stable identity for this animation (drives React key + remount). */
  ghostKey: number;
  /** Pixel left of the source square (top-left of the square rect). */
  fromLeft: number;
  /** Pixel top of the source square. */
  fromTop: number;
  /** Pixel left of the destination square. */
  toLeft: number;
  /** Pixel top of the destination square. */
  toTop: number;
  /** Square edge length in pixels (matches Board.SQUARE_SIZE). */
  squareSize: number;
  /** Whether the move was a capture (renders an additional fading ring burst). */
  isCapture: boolean;
  /** Visual mode. `piece` renders a real SVG (viewer's own move); `fog` renders a non-identifying shroud (opponent move). */
  mode: MoveGhostMode;
  /** Piece type — required only when `mode === 'piece'`. */
  type?: PieceType;
  /** Piece color — required only when `mode === 'piece'`. */
  color?: Color;
  /** Fired once the slide finishes so the parent can tear the overlay down. */
  onComplete: () => void;
}

/**
 * MoveGhost — Transient, non-interactive overlay that animates a single move
 * from its source square to its destination square over ~180ms.
 *
 * Design contract (Fog Chess / Fog-of-War safety):
 * - `mode: 'piece'` is ONLY used for the viewer's own move, where the piece
 *   type is known from `ownPieces`. Rendering the opponent's piece would leak
 *   identity, because the server's `move:applied` payload (`VisibleMove`) is
 *   "geometry only, no piece identity revealed".
 * - `mode: 'fog'` is used for the opponent's move: a blurred, neutral shroud
 *   (reusing the FogOverlay aesthetic) sweeps from→to — tactile, zero leak.
 *
 * The overlay is `pointerEvents: none` and lives above the settled board; the
 * underlying memoized squares keep rendering the authoritative state in
 * parallel, so the ghost's fade-out hands off seamlessly to the real piece.
 *
 * It contains ZERO hooks beyond Framer Motion's rendered element and is wrapped
 * in React.memo so unrelated parent re-renders never re-trigger it.
 */
export const MoveGhost = memo(function MoveGhost({
  ghostKey,
  fromLeft,
  fromTop,
  toLeft,
  toTop,
  squareSize,
  isCapture,
  mode,
  type,
  color,
  onComplete,
}: MoveGhostProps) {
  // Slide duration honoring the blueprint's 150-200ms target.
  const slideDuration = 0.18;

  return (
    <motion.div
      key={ghostKey}
      initial={{ left: fromLeft, top: fromTop, opacity: 1 }}
      animate={{ left: toLeft, top: toTop, opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: slideDuration, ease: 'easeOut' }}
      onAnimationComplete={onComplete}
      style={{
        position: 'absolute',
        width: squareSize,
        height: squareSize,
        pointerEvents: 'none',
        // Above settled pieces, below the active drag layer (zIndex 1000).
        zIndex: 50,
      }}
      aria-hidden="true"
    >
      {/* Piece SVG (viewer's own move only) */}
      {mode === 'piece' && type && color && (
        <Piece type={type} color={color} size={squareSize} isDragging={false} />
      )}

      {/* Non-identifying fog shroud (opponent move only) */}
      {mode === 'fog' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            background:
              'linear-gradient(135deg, hsl(var(--muted) / 0.85) 0%, hsl(var(--muted) / 0.92) 50%, hsl(var(--muted) / 0.85) 100%)',
            backdropFilter: 'blur(1.5px)',
            WebkitBackdropFilter: 'blur(1.5px)',
          }}
        >
          {/* Noise texture matching FogOverlay for visual continuity */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E\")",
              opacity: 0.6,
              pointerEvents: 'none',
            }}
          />
        </div>
      )}

      {/* Capture burst: a non-identifying fading ring at the destination.
          Shown only on captures; never reveals piece identity. */}
      {isCapture && (
        <motion.div
          initial={{ scale: 0.4, opacity: 0.55 }}
          animate={{ scale: 1.15, opacity: 0 }}
          transition={{ duration: slideDuration, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            boxShadow: '0 0 0 2px hsl(var(--destructive) / 0.5) inset',
            pointerEvents: 'none',
          }}
        />
      )}
    </motion.div>
  );
}, (prev, next) => {
  // Re-mount only when any animation parameter changes; ghostKey alone would
  // suffice, but comparing the rest is cheap and keeps the comparator explicit.
  return (
    prev.ghostKey === next.ghostKey &&
    prev.fromLeft === next.fromLeft &&
    prev.fromTop === next.fromTop &&
    prev.toLeft === next.toLeft &&
    prev.toTop === next.toTop &&
    prev.squareSize === next.squareSize &&
    prev.isCapture === next.isCapture &&
    prev.mode === next.mode &&
    prev.type === next.type &&
    prev.color === next.color &&
    prev.onComplete === next.onComplete
  );
});

MoveGhost.displayName = 'MoveGhost';
